"use client";

import { useState, useEffect } from "react";

export type DeviceType = "mobile" | "tablet" | "laptop" | "desktop";
export type Orientation = "portrait" | "landscape";

interface DeviceInfo {
  type: DeviceType;
  orientation: Orientation;
  isTouchDevice: boolean;
  screenWidth: number;
  screenHeight: number;
  isIOS: boolean;
  isAndroid: boolean;
  isIPad: boolean;
  isIPhone: boolean;
  isMobileDevice: boolean;
  isTabletDevice: boolean;
  pixelRatio: number;
}

interface DeviceBreakpoints {
  mobile: { maxWidth: number };
  tablet: { minWidth: number; maxWidth: number };
  laptop: { minWidth: number; maxWidth: number };
  desktop: { minWidth: number };
}

const DEFAULT_BREAKPOINTS: DeviceBreakpoints = {
  mobile: { maxWidth: 639 },
  tablet: { minWidth: 640, maxWidth: 1023 },
  laptop: { minWidth: 1024, maxWidth: 1439 },
  desktop: { minWidth: 1440 },
};

/**
 * Detects if the device is a touch device
 */
const isTouchDevice = (): boolean => {
  if (typeof window === "undefined") return false;
  return (
    "ontouchstart" in window ||
    navigator.maxTouchPoints > 0 ||
    (navigator as any).msMaxTouchPoints > 0
  );
};

/**
 * Detects if running on iOS
 */
const detectIOS = (): boolean => {
  if (typeof window === "undefined") return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
};

/**
 * Detects if running on Android
 */
const detectAndroid = (): boolean => {
  if (typeof window === "undefined") return false;
  return /Android/.test(navigator.userAgent);
};

/**
 * Detects if running on iPad specifically
 */
const detectIPad = (): boolean => {
  if (typeof window === "undefined") return false;
  // Modern iPads report as MacIntel but have touch
  const isModernIPad =
    navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  const isLegacyIPad = /iPad/.test(navigator.userAgent);
  return isModernIPad || isLegacyIPad;
};

/**
 * Detects if running on iPhone specifically
 */
const detectIPhone = (): boolean => {
  if (typeof window === "undefined") return false;
  return /iPhone/.test(navigator.userAgent);
};

/**
 * Detects if the device is a mobile device based on user agent
 */
const detectMobileUA = (): boolean => {
  if (typeof window === "undefined") return false;
  return /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
};

/**
 * Detects if the device is a tablet based on user agent and screen size
 */
const detectTabletUA = (): boolean => {
  if (typeof window === "undefined") return false;
  const isIPad = detectIPad();
  const isAndroidTablet =
    /Android/.test(navigator.userAgent) &&
    !/Mobile/.test(navigator.userAgent);
  return isIPad || isAndroidTablet;
};

/**
 * Get the current orientation
 */
const getOrientation = (): Orientation => {
  if (typeof window === "undefined") return "portrait";
  return window.innerWidth > window.innerHeight ? "landscape" : "portrait";
};

/**
 * Determine device type based on screen width, touch capability, and user agent
 */
const getDeviceType = (width: number): DeviceType => {
  const isTouch = isTouchDevice();
  const isMobileUA = detectMobileUA();
  const isTabletUA = detectTabletUA();
  const isIPad = detectIPad();

  // iPad detection takes priority - iPads should always be treated as tablets
  if (isIPad) {
    return "tablet";
  }

  // Mobile phones (touch + small screen + mobile UA)
  if (width <= DEFAULT_BREAKPOINTS.mobile.maxWidth) {
    return "mobile";
  }

  // Tablet range OR tablet user agent with touch
  if (
    (width >= DEFAULT_BREAKPOINTS.tablet.minWidth &&
      width <= DEFAULT_BREAKPOINTS.tablet.maxWidth) ||
    (isTabletUA && isTouch)
  ) {
    return "tablet";
  }

  // Touch device in laptop range - likely a tablet in landscape
  if (
    width >= DEFAULT_BREAKPOINTS.laptop.minWidth &&
    width <= DEFAULT_BREAKPOINTS.laptop.maxWidth
  ) {
    // If it's a touch device with tablet UA, treat as tablet
    if (isTouch && (isTabletUA || isMobileUA)) {
      return "tablet";
    }
    return "laptop";
  }

  // Large screens
  if (width >= DEFAULT_BREAKPOINTS.desktop.minWidth) {
    // Even large touch screens without tablet UA are desktops (like Surface Studio)
    return "desktop";
  }

  // Default to laptop for anything else
  return "laptop";
};

/**
 * Get current device info
 */
const getCurrentDeviceInfo = (): DeviceInfo => {
  if (typeof window === "undefined") {
    return {
      type: "desktop",
      orientation: "landscape",
      isTouchDevice: false,
      screenWidth: 1920,
      screenHeight: 1080,
      isIOS: false,
      isAndroid: false,
      isIPad: false,
      isIPhone: false,
      isMobileDevice: false,
      isTabletDevice: false,
      pixelRatio: 1,
    };
  }

  const width = window.innerWidth;
  const height = window.innerHeight;

  return {
    type: getDeviceType(width),
    orientation: getOrientation(),
    isTouchDevice: isTouchDevice(),
    screenWidth: width,
    screenHeight: height,
    isIOS: detectIOS(),
    isAndroid: detectAndroid(),
    isIPad: detectIPad(),
    isIPhone: detectIPhone(),
    isMobileDevice: detectMobileUA(),
    isTabletDevice: detectTabletUA(),
    pixelRatio: window.devicePixelRatio || 1,
  };
};

/**
 * Hook for smart device detection with real-time updates
 */
export const useDeviceDetection = () => {
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>(getCurrentDeviceInfo);

  useEffect(() => {
    // Debounced resize handler
    let resizeTimeout: ReturnType<typeof setTimeout>;

    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const newInfo = getCurrentDeviceInfo();
        setDeviceInfo((prev) => {
          // Only update if values actually changed
          if (
            prev.type === newInfo.type &&
            prev.orientation === newInfo.orientation &&
            prev.screenWidth === newInfo.screenWidth &&
            prev.screenHeight === newInfo.screenHeight &&
            prev.pixelRatio === newInfo.pixelRatio
          ) {
            return prev;
          }
          return newInfo;
        });
      }, 150);
    };

    // Orientation change handler
    const handleOrientationChange = () => {
      // Small delay to let the browser update dimensions
      setTimeout(() => {
        const newInfo = getCurrentDeviceInfo();
        setDeviceInfo((prev) => {
          if (
            prev.type === newInfo.type &&
            prev.orientation === newInfo.orientation &&
            prev.screenWidth === newInfo.screenWidth &&
            prev.screenHeight === newInfo.screenHeight
          ) {
            return prev;
          }
          return newInfo;
        });
      }, 150);
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleOrientationChange);

    // Also listen for screen orientation API if available
    if (screen.orientation) {
      screen.orientation.addEventListener("change", handleOrientationChange);
    }

    return () => {
      clearTimeout(resizeTimeout);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleOrientationChange);
      if (screen.orientation) {
        screen.orientation.removeEventListener("change", handleOrientationChange);
      }
    };
  }, []); // Empty dependency array - only setup once

  // Helper functions for common checks
  const isMobile = deviceInfo.type === "mobile";
  const isTablet = deviceInfo.type === "tablet";
  const isLaptop = deviceInfo.type === "laptop";
  const isDesktop = deviceInfo.type === "desktop";
  const isMobileOrTablet = isMobile || isTablet;
  const isLaptopOrDesktop = isLaptop || isDesktop;

  return {
    ...deviceInfo,
    isMobile,
    isTablet,
    isLaptop,
    isDesktop,
    isMobileOrTablet,
    isLaptopOrDesktop,
    breakpoints: DEFAULT_BREAKPOINTS,
  };
};

export default useDeviceDetection;
